import html2canvas from "html2canvas";
import jsPDF from "jspdf";

/**
 * Render a single DOM element (which contains one or more .a4-sheet pages)
 * to a PDF. Each .a4-sheet is captured, scaled to exactly one A4 page, and
 * added to the PDF. Blank trailing pages are avoided by clamping tiny
 * over-height rounding artifacts (< ~4 mm) and only slicing to multiple
 * pages when content genuinely overflows one A4 sheet.
 *
 * File-size optimization:
 *   - html2canvas @ scale 1.75 (still print-crisp, smaller raster)
 *   - JPEG encoding @ 0.9 quality (dramatically smaller than PNG for
 *     text-on-white court documents; visually identical for print)
 *   - jsPDF { compress: true }
 *   - html2canvas { removeContainer: true } cleans up temp DOM
 *
 * Layout, fonts, spacing, margins, translations, previews are NOT changed.
 */

const A4_HEIGHT_TOLERANCE_MM = 4; // clamp small rounding overflow to 1 page
const PAGE_SLICE_MIN_REMAINING_MM = 4; // don't add pages for <4mm leftovers

export async function generatePdfFromElement(element, filename, options = {}) {
  const { returnDoc = false } = options;
  if (!element) throw new Error("PDF source element is missing.");

  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
    compress: true,
  });

  const pages = element.querySelectorAll(".a4-sheet");
  const sheets = pages.length > 0 ? Array.from(pages) : [element];

  for (let i = 0; i < sheets.length; i += 1) {
    const sheet = sheets[i];
    const canvas = await html2canvas(sheet, {
      scale: 1.75,
      useCORS: true,
      backgroundColor: "#ffffff",
      windowWidth: sheet.scrollWidth,
      windowHeight: sheet.scrollHeight,
      removeContainer: true,
      logging: false,
    });
    const imgData = canvas.toDataURL("image/jpeg", 0.9);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgRatio = canvas.height / canvas.width;
    const imgWidth = pdfWidth;
    let imgHeight = pdfWidth * imgRatio;

    if (i > 0) pdf.addPage();

    // Clamp tiny rounding overflow so a 1-page sheet never spills to page 2.
    if (imgHeight > pdfHeight && imgHeight - pdfHeight <= A4_HEIGHT_TOLERANCE_MM) {
      imgHeight = pdfHeight;
    }

    if (imgHeight <= pdfHeight) {
      pdf.addImage(imgData, "JPEG", 0, 0, imgWidth, imgHeight, undefined, "FAST");
    } else {
      // Content genuinely overflows one A4 — slice into multiple pages.
      let remaining = imgHeight;
      let position = 0;
      while (remaining > PAGE_SLICE_MIN_REMAINING_MM) {
        pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight, undefined, "FAST");
        remaining -= pdfHeight;
        position -= pdfHeight;
        if (remaining > PAGE_SLICE_MIN_REMAINING_MM) pdf.addPage();
      }
    }
  }

  if (returnDoc) return pdf;
  pdf.save(filename);
  return pdf;
}
